import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '@/App';
import { useTranslation } from '@/hooks/useTranslation';
import { useTrialStatus } from '@/context/TrialContext';
import { TrialGatedButton } from '@/components/TrialExpiredBanner';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from '@/components/ui/drawer';
import Sidebar from '@/components/Sidebar';
import DigitalTabs from '@/components/DigitalTabs';
import ImageUpload from '@/components/ImageUpload';
import {
  Package,
  Plus,
  Search,
  Edit2,
  Trash2,
  Image,
  Tag,
  ToggleLeft,
  ToggleRight,
  Menu,
  X,
  Filter,
  Grid,
  List,
  Layers,
  ImageOff,
  Check,
  Pencil
} from 'lucide-react';
import Pagination from '@/components/Pagination';

// Helper function to get proxied image URL
const getProxiedImageUrl = (url) => {
  if (!url) return '';
  return `${API}/upload/proxy?url=${encodeURIComponent(url)}`;
};

// Product Image Component with error handling and proxy fallback
const ProductImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [useProxy, setUseProxy] = useState(false);

  // Generate image URL - use proxy if direct loading failed
  const getImageUrl = useCallback(() => {
    if (!src) return '';
    if (useProxy) {
      // Use backend proxy to bypass CORS/referrer blocking
      return `${API}/upload/proxy?url=${encodeURIComponent(src)}`;
    }
    return src;
  }, [src, useProxy]);

  // Handle image load error
  const handleError = useCallback(() => {
    console.error('Image load error:', src, 'useProxy:', useProxy);
    
    if (!useProxy) {
      // First failure - try with proxy
      console.log('Retrying with proxy...');
      setUseProxy(true);
      setIsLoading(true);
    } else {
      // Proxy also failed - give up
      setHasError(true);
      setIsLoading(false);
    }
  }, [src, useProxy]);

  if (!src || src.trim() === '' || hasError) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 dark:text-zinc-400">
        <ImageOff className="w-10 h-10 mb-1" />
        <span className="text-xs">{hasError ? 'Gagal memuat' : 'Tidak ada gambar'}</span>
      </div>
    );
  }

  return (
    <>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-zinc-800">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <img
        key={`${src}-${useProxy ? 'proxy' : 'direct'}`}
        src={getImageUrl()}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        loading="eager"
        onLoad={() => {
          setIsLoading(false);
          setHasError(false);
        }}
        onError={handleError}
      />
    </>
  );
};

// Product Form Component - defined outside to prevent re-render issues
const ProductForm = ({ 
  formData, 
  setFormData, 
  newVariant, 
  setNewVariant, 
  categories, 
  onSubmit, 
  onCancel,
  submitLabel,
  businessType = 'physical'
}) => {
  const hasVariants = formData.variants && formData.variants.length > 0;
  const [enableVariants, setEnableVariants] = React.useState(hasVariants);
  const [editingVariantIndex, setEditingVariantIndex] = React.useState(null);
  const isDigital = formData.fulfillment_type === 'digital';
  const showFulfillmentToggle = businessType === 'both';
  const { token: authToken } = useAuth();
  const [assets, setAssets] = React.useState([]);
  const [pools, setPools] = React.useState([]);
  const digitalType = formData.digital_type || 'file';
  const config = formData.digital_config || {};

  React.useEffect(() => {
    if (!isDigital) return;
    const headers = { Authorization: `Bearer ${authToken}` };
    Promise.all([
      axios.get(`${API}/digital/assets`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/digital/code-pools`, { headers }).catch(() => ({ data: {} })),
    ]).then(([a, p]) => {
      setAssets(a.data.assets || []);
      setPools(p.data.pools || []);
    });
  }, [isDigital, authToken]);

  const setConfig = (patch) => setFormData({ ...formData, digital_config: { ...config, ...patch } });

  const formatPrice = (price) => {
    let numericPrice = price;
    if (typeof price === 'string') {
      numericPrice = parseFloat(price.replace(/[Rp\s.]/g, '').replace(',', '.')) || 0;
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(numericPrice);
  };

  const addVariant = () => {
    if (!newVariant.name) return;
    const variant = {
      name: newVariant.name,
      price: parseFloat(newVariant.price) || 0,
      stock: parseInt(newVariant.stock) || 0
    };
    const variants = editingVariantIndex != null
      ? formData.variants.map((v, i) => (i === editingVariantIndex ? variant : v))
      : [...formData.variants, variant];
    setFormData({ ...formData, variants });
    setNewVariant({ name: '', price: '', stock: '' });
    setEditingVariantIndex(null);
  };

  const editVariant = (index) => {
    const v = formData.variants[index];
    setNewVariant({ name: v.name || '', price: v.price ?? '', stock: v.stock ?? '' });
    setEditingVariantIndex(index);
  };

  const removeVariant = (index) => {
    const updated = formData.variants.filter((_, i) => i !== index);
    setFormData({ ...formData, variants: updated });
    setEditingVariantIndex(null);
    setNewVariant({ name: '', price: '', stock: '' });
    if (updated.length === 0) setEnableVariants(false);
  };

  const toggleVariants = (enabled) => {
    setEnableVariants(enabled);
    if (!enabled) {
      setFormData({ ...formData, variants: [] });
    }
  };

  return (
    <div className="space-y-4">
      {showFulfillmentToggle && (
        <div className="grid grid-cols-2 gap-2" data-testid="product-fulfillment-toggle">
          {[
            { value: 'physical', label: 'Produk Fisik', note: 'Dikirim kurir, butuh berat' },
            { value: 'digital', label: 'Produk Digital', note: 'Dikirim ke email, tanpa ongkir' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setFormData({ ...formData, fulfillment_type: opt.value, weight: opt.value === 'digital' ? '' : formData.weight })}
              data-testid={`fulfillment-${opt.value}-btn`}
              className={`text-left rounded-xl border p-3 transition-colors ${formData.fulfillment_type === opt.value
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 dark:border-indigo-500'
                : 'border-slate-200 hover:border-slate-300 dark:border-zinc-800 dark:hover:border-zinc-700'}`}
            >
              <p className="text-sm font-medium text-slate-800 dark:text-zinc-200">{opt.label}</p>
              <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">{opt.note}</p>
            </button>
          ))}
        </div>
      )}
      {isDigital && (
        <div className="rounded-xl border border-slate-200 dark:border-zinc-800 p-3 space-y-3" data-testid="digital-config-section">
          <div>
            <Label className="text-sm">Cara kirim ke pembeli</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[
                { value: 'file', label: 'File' },
                { value: 'license_code', label: 'Kode Lisensi' },
                { value: 'link_access', label: 'Link Akses' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, digital_type: opt.value })}
                  data-testid={`digital-type-${opt.value}-btn`}
                  className={`px-2 py-2 rounded-lg border text-xs font-medium transition-colors ${digitalType === opt.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300 dark:border-zinc-800 dark:text-zinc-300'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {digitalType === 'file' && (
            <>
              <div>
                <Label className="text-sm">File yang dikirim</Label>
                <select
                  value={config.asset_id || ''}
                  onChange={(e) => setConfig({ asset_id: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-900 text-sm text-slate-900 dark:text-zinc-50"
                  data-testid="digital-asset-select"
                >
                  <option value="">-- Pilih file --</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>{a.file_name}</option>
                  ))}
                </select>
                {assets.length === 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Belum ada file. Upload dulu di menu <strong>Produk Digital</strong>.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Maks. unduh</Label>
                  <Input type="number" min="1" value={config.download_limit ?? 5}
                    onChange={(e) => setConfig({ download_limit: parseInt(e.target.value) || 1 })}
                    className="mt-1" data-testid="download-limit-input" />
                </div>
                <div>
                  <Label className="text-sm">Berlaku (jam)</Label>
                  <Input type="number" min="1" value={config.link_ttl_hours ?? 168}
                    onChange={(e) => setConfig({ link_ttl_hours: parseInt(e.target.value) || 1 })}
                    className="mt-1" data-testid="link-ttl-input" />
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">168 jam = 7 hari</p>
                </div>
              </div>
            </>
          )}

          {digitalType === 'license_code' && (
            <div>
              <Label className="text-sm">Stok kode yang dipakai</Label>
              <select
                value={config.code_pool_id || ''}
                onChange={(e) => setConfig({ code_pool_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-900 text-sm text-slate-900 dark:text-zinc-50"
                data-testid="code-pool-select"
              >
                <option value="">-- Pilih stok kode --</option>
                {pools.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} (sisa {p.available})</option>
                ))}
              </select>
              {pools.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Belum ada stok kode. Buat dulu di menu <strong>Produk Digital</strong>.
                </p>
              )}
            </div>
          )}

          {digitalType === 'link_access' && (
            <div>
              <Label className="text-sm">Link akses</Label>
              <Input value={config.access_url || ''} onChange={(e) => setConfig({ access_url: e.target.value })}
                placeholder="https://t.me/+xxxxx" className="mt-1" data-testid="access-url-input" />
            </div>
          )}

          <div>
            <Label className="text-sm">Catatan untuk pembeli (opsional)</Label>
            <Textarea value={config.access_instruction || ''}
              onChange={(e) => setConfig({ access_instruction: e.target.value })}
              placeholder="Contoh: buka link lalu ketik /start untuk gabung grup"
              rows={2} className="mt-1" data-testid="access-instruction-input" />
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Nama Produk *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Kaos Polos Premium"
            className="mt-1"
            data-testid="product-name-input"
          />
        </div>
        
        {/* Variant Toggle — di ATAS harga & stok supaya seller tidak mengisi harga dua kali */}
        <div className="col-span-2">
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="variant-toggle" onClick={() => toggleVariants(!enableVariants)}>
            <div className={`relative w-11 h-6 rounded-full transition-colors ${enableVariants ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-zinc-700'}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enableVariants ? 'translate-x-5' : 'translate-x-0'} dark:bg-zinc-900`} />
            </div>
            <span className="text-sm font-medium text-slate-700 dark:text-zinc-300 flex items-center gap-2">
              <Layers className="w-4 h-4" />
              Produk ini memiliki varian
            </span>
          </label>
        </div>

        {/* Variant Section */}
        {enableVariants && (
          <div className="col-span-2 border border-slate-200 dark:border-zinc-800 rounded-xl p-4 space-y-3 bg-slate-50 dark:bg-zinc-900/50">
            <p className="text-xs text-slate-500 dark:text-zinc-400 mb-2">
              Setiap varian memiliki harga dan stok masing-masing
            </p>
            
            {formData.variants.map((variant, index) => (
              <div key={index} className="flex items-center gap-2 bg-white dark:bg-zinc-900 rounded-lg p-2.5 border border-slate-200 dark:border-zinc-800">
                <span className="flex-1 text-sm font-medium text-slate-700 dark:text-zinc-300">{variant.name}</span>
                <Badge className="bg-indigo-100 text-indigo-700 dark:bg-blue-900/50 dark:text-indigo-400 font-semibold">
                  {formatPrice(variant.price || 0)}
                </Badge>
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400 text-xs">
                  Stok: {variant.stock ?? 0}
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editVariant(index)}
                  className="h-6 w-6 p-0 text-slate-500 hover:text-indigo-600 dark:text-zinc-400"
                  type="button"
                  title="Edit varian"
                  data-testid={`edit-variant-${index}`}
                >
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeVariant(index)}
                  className="h-6 w-6 p-0 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                  type="button"
                  title="Hapus varian"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
            
            <div className="flex gap-2">
              <Input
                value={newVariant.name}
                onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
                placeholder="Nama (M, L, Merah...)"
                className="flex-1"
                data-testid="variant-name-input"
              />
              <Input
                type="number"
                value={newVariant.price}
                onChange={(e) => setNewVariant({ ...newVariant, price: e.target.value })}
                placeholder="Harga"
                className="w-28"
                data-testid="variant-price-input"
              />
              <Input
                type="number"
                value={newVariant.stock}
                onChange={(e) => setNewVariant({ ...newVariant, stock: e.target.value })}
                placeholder="Stok"
                className="w-20"
                data-testid="variant-stock-input"
              />
            </div>
            <div className="flex gap-2">
              {editingVariantIndex != null && (
                <Button variant="ghost" size="sm" type="button" onClick={() => { setEditingVariantIndex(null); setNewVariant({ name: '', price: '', stock: '' }); }} data-testid="cancel-variant-btn">
                  Batal
                </Button>
              )}
              <Button variant="outline" size="sm" className="flex-1 gap-2" onClick={addVariant} disabled={!newVariant.name} type="button" data-testid="add-variant-btn">
                {editingVariantIndex != null ? <><Check className="w-4 h-4" />Simpan Varian</> : <><Plus className="w-4 h-4" />Tambah Varian</>}
              </Button>
            </div>
          </div>
        )}

        {!enableVariants && (
          <>
            <div>
              <Label>Harga *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="75000"
                className="mt-1"
                data-testid="product-price-input"
              />
            </div>
            {/* Produk digital tidak punya stok fisik: link/file/ebook tak terbatas,
                kode lisensi dilacak di halaman Produk Digital. */}
            {!isDigital && (
              <div>
                <Label>Stok</Label>
                <Input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  placeholder="100"
                  className="mt-1"
                  data-testid="product-stock-input"
                />
              </div>
            )}
          </>
        )}
        
        {!isDigital && (
        <div>
          <Label>Berat (gram)</Label>
          <Input
            type="number"
            value={formData.weight}
            onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
            placeholder="500"
            className="mt-1"
          />
          <p className="text-xs text-slate-500 mt-1 dark:text-zinc-400">Untuk perhitungan ongkir</p>
        </div>
        )}
        
        {!enableVariants && !isDigital && <div />}
        
        <div className="col-span-2">
          <Label>Kategori</Label>
          <Input
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            placeholder="Fashion"
            list="categories-list"
            className="mt-1"
          />
          <datalist id="categories-list">
            {categories.map((cat) => (
              <option key={cat} value={cat} />
            ))}
          </datalist>
        </div>
        
        <div className="col-span-2">
          <Label>Gambar Produk</Label>
          
          <div className="mt-2">
            <ImageUpload
              value={formData.image_url}
              onChange={(url) => setFormData({ ...formData, image_url: url })}
            />
          </div>
        </div>
        
        <div className="col-span-2">
          <Label>Deskripsi</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Deskripsi produk..."
            className="mt-1"
            rows={3}
          />
        </div>

      </div>
      
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel} type="button">
          Batal
        </Button>
        <Button onClick={onSubmit} className="bg-indigo-600 hover:bg-indigo-700" type="button" data-testid="product-submit-btn">
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};

const CatalogPage = ({ variant = 'auto' }) => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const { isTrialExpired } = useTrialStatus();
  // Menu produk terpisah per jenis: /catalog = fisik, /digital/products = digital.
  const productKind = variant !== 'auto'
    ? variant
    : (user?.business_type === 'digital' ? 'digital' : 'physical');
  const isDigitalCatalog = productKind === 'digital';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [stats, setStats] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total: 0,
    total_pages: 1,
    has_prev: false,
    has_next: false
  });
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    weight: '',
    category: '',
    image_url: '',
    stock: '',
    fulfillment_type: isDigitalCatalog ? 'digital' : 'physical',
    digital_type: 'file',
    digital_config: { download_limit: 5, link_ttl_hours: 168 },
    variants: []
  });
  const [newVariant, setNewVariant] = useState({ name: '', price: '', stock: '' });

  const fetchProducts = async (page = pagination.page) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ user_id: user.id });
      params.append('page', page.toString());
      params.append('per_page', pagination.per_page.toString());
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      params.append('fulfillment_type', productKind);
      
      const response = await axios.get(
        `${API}/products/list?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setProducts(response.data.products);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(t('products.loadError') || 'Gagal memuat produk');
    } finally {
      setLoading(false);
    }
  };

  // Go to specific page
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      fetchProducts(page);
    }
  };

  // productKind WAJIB masuk dependency: /catalog dan /digital/products memakai komponen yang sama,
  // jadi React hanya mengganti prop `variant` tanpa remount → tanpa ini daftar & KPI tetap
  // menampilkan data jenis produk sebelumnya.
  useEffect(() => {
    setProducts([]);
    setStats(null);
    fetchProducts(1);
    fetchCategories();
    fetchStats();
  }, [searchQuery, selectedCategory, productKind]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${API}/products/categories?user_id=${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API}/products/stats/summary?user_id=${user.id}&fulfillment_type=${productKind}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const digitalConfigError = (data) => {
    if (data.fulfillment_type !== 'digital') return null;
    const type = data.digital_type || 'file';
    const cfg = data.digital_config || {};
    if (type === 'file' && !cfg.asset_id) return 'Pilih file yang akan dikirim ke pembeli';
    if (type === 'license_code' && !cfg.code_pool_id) return 'Pilih stok kode lisensi';
    if (type === 'link_access' && !cfg.access_url) return 'Isi link akses untuk pembeli';
    return null;
  };

  const handleCreateProduct = async () => {
    const hasVariants = formData.variants && formData.variants.length > 0;
    
    if (!formData.name || (!hasVariants && !formData.price)) {
      toast.error(t('products.nameAndPriceRequired') || 'Nama dan harga wajib diisi');
      return;
    }
    const digitalError = digitalConfigError(formData);
    if (digitalError) {
      toast.error(digitalError);
      return;
    }

    try {
      const payload = {
        ...formData,
        price: hasVariants ? null : parseFloat(formData.price),
        weight: formData.weight ? parseInt(formData.weight) : null,
        stock: (isDigitalCatalog || formData.fulfillment_type === 'digital' || hasVariants)
          ? null
          : (formData.stock ? parseInt(formData.stock) : null),
        variants: formData.variants.map(v => ({
          name: v.name,
          price: parseFloat(v.price) || 0,
          stock: parseInt(v.stock) || 0,
        }))
      };
      
      const response = await axios.post(
        `${API}/products/create?user_id=${user.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(t('products.productCreated'));
        setCreateDialogOpen(false);
        resetForm();
        fetchProducts();
        fetchCategories();
        fetchStats();
      }
    } catch (error) {
      toast.error(t('products.createFailed') || 'Gagal menambahkan produk');
    }
  };

  const handleUpdateProduct = async () => {
    const hasVariants = formData.variants && formData.variants.length > 0;
    
    if (!formData.name || (!hasVariants && !formData.price)) {
      toast.error(t('products.nameAndPriceRequired') || 'Nama dan harga wajib diisi');
      return;
    }
    const digitalError = digitalConfigError(formData);
    if (digitalError) {
      toast.error(digitalError);
      return;
    }

    try {
      const payload = {
        ...formData,
        price: hasVariants ? null : parseFloat(formData.price),
        weight: formData.weight ? parseInt(formData.weight) : null,
        stock: (isDigitalCatalog || formData.fulfillment_type === 'digital' || hasVariants)
          ? null
          : (formData.stock ? parseInt(formData.stock) : null),
        variants: formData.variants.map(v => ({
          name: v.name,
          price: parseFloat(v.price) || 0,
          stock: parseInt(v.stock) || 0,
        }))
      };
      
      const response = await axios.put(
        `${API}/products/${selectedProduct.id}?user_id=${user.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(t('products.productSaved'));
        setEditDialogOpen(false);
        resetForm();
        fetchProducts();
        fetchCategories();
      }
    } catch (error) {
      toast.error(t('products.updateFailed') || 'Gagal mengupdate produk');
    }
  };

  const handleDeleteProduct = async () => {
    try {
      const response = await axios.delete(
        `${API}/products/${selectedProduct.id}?user_id=${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(t('products.productDeleted'));
        setDeleteDialogOpen(false);
        setSelectedProduct(null);
        fetchProducts();
        fetchStats();
      }
    } catch (error) {
      toast.error('Gagal menghapus produk');
    }
  };

  const handleToggleActive = async (product) => {
    try {
      const response = await axios.post(
        `${API}/products/${product.id}/toggle-active?user_id=${user.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.is_active ? 'Produk diaktifkan' : 'Produk dinonaktifkan');
        fetchProducts();
        fetchStats();
      }
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    // Map variants: support both legacy (price_adjustment) and new (price) format
    const mappedVariants = (product.variants || []).map(v => ({
      name: v.name,
      price: v.price != null ? v.price : (product.price + (v.price_adjustment || 0)),
      stock: v.stock != null ? v.stock : (product.stock || 0),
    }));
    setFormData({
      name: product.name,
      description: product.description || '',
      price: product.price?.toString() || '',
      weight: product.weight?.toString() || '',
      category: product.category || '',
      image_url: product.image_url || '',
      stock: product.stock?.toString() || '',
      fulfillment_type: product.fulfillment_type === 'digital' ? 'digital' : 'physical',
      digital_type: product.digital_type || 'file',
      digital_config: product.digital_config || { download_limit: 5, link_ttl_hours: 168 },
      variants: mappedVariants
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (product) => {
    setSelectedProduct(product);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      weight: '',
      category: '',
      image_url: '',
      stock: '',
      fulfillment_type: isDigitalCatalog ? 'digital' : 'physical',
      digital_type: 'file',
      digital_config: { download_limit: 5, link_ttl_hours: 168 },
      variants: []
    });
    setNewVariant({ name: '', price: '', stock: '' });
    setSelectedProduct(null);
  };

  const formatPrice = (price) => {
    // Handle price stored as string like "Rp 150.000" from onboarding
    let numericPrice = price;
    if (typeof price === 'string') {
      // Remove "Rp", spaces, dots (thousand separator), and parse
      numericPrice = parseFloat(price.replace(/[Rp\s.]/g, '').replace(',', '.')) || 0;
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(numericPrice);
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    resetForm();
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="flex-1 lg:ml-64">
        {/* Header - Compact on mobile */}
        <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-30">
          <div className="px-3 sm:px-6 py-2.5 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 dark:bg-zinc-800"
                  data-testid="sidebar-toggle"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>
                  {isDigitalCatalog ? 'Produk Digital' : 'Produk Fisik'}
                </h1>
              </div>
              <div className="flex items-center gap-2">
                {isTrialExpired ? (
                  <TrialGatedButton 
                    className="bg-indigo-600 hover:bg-indigo-700 text-sm px-3 sm:px-4 h-9"
                    tooltipText="Upgrade untuk menambah produk"
                    data-testid="add-product-gated"
                  >
                    <Plus className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('products.addProduct')}</span>
                  </TrialGatedButton>
                ) : (
                  <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-indigo-600 hover:bg-indigo-700 text-sm px-3 sm:px-4 h-9" data-testid="add-product-btn">
                        <Plus className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">{t('products.addProduct')}</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[550px] bg-white dark:bg-zinc-900 max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-bold flex items-center gap-2">
                          <Package className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                          {t('products.addNewProduct')}
                        </DialogTitle>
                      </DialogHeader>
                      <ProductForm 
                        formData={formData}
                        setFormData={setFormData}
                        newVariant={newVariant}
                        setNewVariant={setNewVariant}
                        categories={categories}
                        onSubmit={handleCreateProduct} 
                        onCancel={handleCloseDialog}
                        submitLabel={t('products.addProduct')}
                        businessType={isDigitalCatalog ? 'digital' : 'physical'}
                        t={t}
                      />
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
          </div>
          {isDigitalCatalog && (
            <div className="px-3 sm:px-6 pb-2.5">
              <DigitalTabs active="products" />
            </div>
          )}

          {/* Control Bar: Search + Filter + View Toggle — inline in header on mobile */}
          <div className="px-3 sm:px-6 pb-2.5 sm:pb-4">
            <div className="flex items-center gap-2" data-testid="control-bar">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cari produk..."
                  className="pl-8 h-9 text-sm"
                  data-testid="search-input"
                />
              </div>
              {/* Filter Button — opens bottom sheet on mobile, dropdown on desktop */}
              <button
                onClick={() => setFilterDrawerOpen(true)}
                className={`relative p-2 h-9 w-9 flex items-center justify-center rounded-lg border transition-colors ${
                  selectedCategory
                    ? 'border-blue-500 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600'
                    : 'border-slate-200 dark:border-zinc-800 hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-300'
                }`}
                data-testid="filter-btn"
              >
                <Filter className="w-4 h-4" />
                {selectedCategory && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-indigo-500 rounded-full" />
                )}
              </button>
              {/* View Toggle */}
              <div className="flex h-9 bg-slate-100 dark:bg-zinc-800 rounded-lg p-0.5" data-testid="view-toggle">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-zinc-700 shadow-sm text-slate-900 dark:text-zinc-50' : 'text-slate-500 dark:text-zinc-400'}`}
                  data-testid="view-grid-btn"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-zinc-700 shadow-sm text-slate-900 dark:text-zinc-50' : 'text-slate-500 dark:text-zinc-400'}`}
                  data-testid="view-list-btn"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-3 sm:p-6">
          {/* Stats — hidden on mobile, visible on sm+ */}
          {stats && (
            <div className="hidden sm:grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="stats-section">
              <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm dark:text-zinc-400">{t('products.totalProducts')}</span>
                  <Package className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">
                  {stats.total_products}
                </p>
              </div>
              <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm dark:text-zinc-400">{t('products.active')}</span>
                  <ToggleRight className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">
                  {stats.active_products}
                </p>
              </div>
              <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm dark:text-zinc-400">{t('products.inactive')}</span>
                  <ToggleLeft className="w-5 h-5 text-slate-500 dark:text-zinc-400" />
                </div>
                <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">
                  {stats.inactive_products}
                </p>
              </div>
              <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm dark:text-zinc-400">Kategori</span>
                  <Tag className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">
                  {stats.categories?.length || 0}
                </p>
              </div>
            </div>
          )}

          {/* Active filter chip on mobile */}
          {selectedCategory && (
            <div className="flex items-center gap-2 mb-3 sm:hidden" data-testid="active-filter-chip">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-full text-sm text-indigo-700 dark:text-indigo-300">
                <Tag className="w-3 h-3" />
                <span>{selectedCategory}</span>
                <button onClick={() => setSelectedCategory('')} className="ml-0.5 hover:text-blue-900 dark:hover:text-blue-100 dark:text-blue-300">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* Desktop-only filter row (category dropdown) */}
          {categories.length > 0 && (
            <div className="hidden sm:flex items-center gap-2 mb-4" data-testid="desktop-category-filter">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-slate-900 dark:text-zinc-50 text-sm"
                data-testid="category-select"
              >
                <option value="">Semua Kategori</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          )}

          {/* Products Grid/List */}
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-8 sm:p-12 text-center" data-testid="empty-state">
              <Package className="w-12 h-12 sm:w-16 sm:h-16 text-slate-500 dark:text-zinc-500 mx-auto mb-3" />
              <h3 className="text-base sm:text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-1">
                Belum ada produk
              </h3>
              <p className="text-sm text-slate-500 dark:text-zinc-400 mb-4">
                Tambahkan produk pertama Anda untuk AI Agent
              </p>
              <TrialGatedButton 
                onClick={() => setCreateDialogOpen(true)} 
                className="bg-indigo-600 hover:bg-indigo-700"
                tooltipText="Upgrade untuk menambah produk"
                data-testid="empty-add-product-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Produk
              </TrialGatedButton>
            </div>
          ) : viewMode === 'grid' ? (
            /* Grid View — Compact mobile cards */
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-4" data-testid="product-grid">
              {products.map((product) => (
                <div
                  key={product.id}
                  className={`bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden hover:shadow-md transition-shadow flex flex-col ${!product.is_active ? 'opacity-50' : ''}`}
                  data-testid={`product-card-${product.id}`}
                >
                  {/* Image — shorter aspect ratio on mobile */}
                  <div className="aspect-[4/3] sm:aspect-square bg-slate-100 dark:bg-zinc-800 relative flex-shrink-0 overflow-hidden">
                    <ProductImage src={product.image_url} alt={product.name} />
                    {!product.is_active && (
                      <div className="absolute top-1.5 left-1.5">
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Nonaktif</Badge>
                      </div>
                    )}
                    {product.category && (
                      <div className="absolute top-1.5 right-1.5">
                        <Badge className="bg-indigo-600 text-white border-transparent text-[10px] px-1.5 py-0">{product.category}</Badge>
                      </div>
                    )}
                    {product.fulfillment_type === 'digital' && (
                      <div className="absolute bottom-1.5 left-1.5">
                        <Badge className="bg-sky-600 text-white border-transparent text-[10px] px-1.5 py-0" data-testid={`product-digital-badge-${product.id}`}>Digital</Badge>
                      </div>
                    )}
                  </div>

                  {/* Content — tighter padding */}
                  <div className="p-2.5 sm:p-3 flex flex-col flex-grow">
                    <h3 className="font-semibold text-slate-900 dark:text-zinc-50 text-sm truncate leading-tight" data-testid={`product-name-${product.id}`}>
                      {product.name}
                    </h3>
                    <p className="text-sm sm:text-base font-bold text-indigo-600 mt-0.5 dark:text-indigo-400">
                      {product.variants?.length > 0 ? (() => {
                        const prices = product.variants.map(v => v.price).filter(p => p != null && p > 0);
                        if (prices.length === 0) return formatPrice(product.price);
                        const min = Math.min(...prices);
                        const max = Math.max(...prices);
                        return min === max ? formatPrice(min) : `${formatPrice(min)} - ${formatPrice(max)}`;
                      })() : formatPrice(product.price)}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[11px]">
                      {product.variants?.length > 0 && (
                        <span className="text-slate-500 dark:text-zinc-400">{product.variants.length} varian</span>
                      )}
                      {product.stock !== null && product.stock === 0 && (
                        <span className="px-1.5 py-0 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full font-medium">
                          Habis
                        </span>
                      )}
                      {product.stock !== null && product.stock > 0 && product.stock <= 5 && (
                        <span className="px-1.5 py-0 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full font-medium">
                          Sisa {product.stock}
                        </span>
                      )}
                      {product.stock !== null && product.stock > 5 && (
                        <span className="text-slate-500 dark:text-zinc-400">Stok: {product.stock}</span>
                      )}
                    </div>

                    {/* Actions — compact row */}
                    <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100 dark:border-zinc-800">
                      <button
                        onClick={() => handleToggleActive(product)}
                        className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors dark:bg-zinc-800"
                        data-testid={`toggle-active-${product.id}`}
                      >
                        {product.is_active ? (
                          <ToggleRight className="w-4 h-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <ToggleLeft className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
                        )}
                      </button>
                      <button
                        onClick={() => openEditDialog(product)}
                        className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors dark:bg-zinc-800 dark:text-indigo-400"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openDeleteDialog(product)}
                        className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-500 hover:text-red-500 transition-colors ml-auto dark:bg-red-950/40 dark:text-zinc-400"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden" data-testid="product-list">
              <div className="divide-y divide-slate-100 dark:divide-zinc-800">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className={`flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3 hover:bg-slate-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-colors ${!product.is_active ? 'opacity-50' : ''} dark:bg-zinc-900`}
                    onClick={() => openEditDialog(product)}
                    data-testid={`product-row-${product.id}`}
                  >
                    {/* Thumbnail */}
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-slate-100 dark:bg-zinc-800 overflow-hidden flex-shrink-0 relative">
                      {product.image_url ? (
                        <img 
                          src={getProxiedImageUrl(product.image_url)} 
                          alt="" 
                          className="w-full h-full object-cover"
                          loading="eager"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full items-center justify-center absolute inset-0 ${product.image_url ? 'hidden' : 'flex'}`}>
                        <Image className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
                      </div>
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-zinc-50 text-sm truncate">{product.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                          {product.variants?.length > 0 ? (() => {
                            const prices = product.variants.map(v => v.price).filter(p => p != null && p > 0);
                            if (prices.length === 0) return formatPrice(product.price);
                            const min = Math.min(...prices);
                            const max = Math.max(...prices);
                            return min === max ? formatPrice(min) : `${formatPrice(min)} - ${formatPrice(max)}`;
                          })() : formatPrice(product.price)}
                        </span>
                        {product.category && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 hidden sm:inline-flex">{product.category}</Badge>
                        )}
                        {product.fulfillment_type === 'digital' && (
                          <span className="text-[10px] px-1.5 py-0 bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 rounded font-medium">Digital</span>
                        )}
                        {product.stock !== null && product.stock === 0 && (
                          <span className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded font-medium">
                            Habis
                          </span>
                        )}
                        {product.stock !== null && product.stock > 0 && product.stock <= 5 && (
                          <span className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded font-medium">
                            Sisa {product.stock}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleToggleActive(product)} className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800 dark:bg-zinc-800">
                        {product.is_active ? <ToggleRight className="w-4 h-4 text-green-600 dark:text-green-400" /> : <ToggleLeft className="w-4 h-4 text-slate-500 dark:text-zinc-400" />}
                      </button>
                      <button onClick={() => openEditDialog(product)} className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 hidden sm:block dark:bg-zinc-800 dark:text-indigo-400">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => openDeleteDialog(product)} className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-500 hover:text-red-500 dark:bg-red-950/40 dark:text-zinc-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          <div className="mt-3 sm:mt-4">
            <Pagination 
              pagination={pagination}
              onPageChange={goToPage}
              itemLabel="produk"
            />
          </div>
        </main>
      </div>

      {/* Filter Bottom Sheet (Drawer) — mobile-first category filter */}
      <Drawer open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <DrawerContent className="bg-white dark:bg-zinc-900">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-base font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2">
              <Filter className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              Filter Kategori
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-1 max-h-[60vh] overflow-y-auto" data-testid="filter-drawer-content">
            {/* All Categories */}
            <button
              onClick={() => { setSelectedCategory(''); setFilterDrawerOpen(false); }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                !selectedCategory
                  ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-medium'
                  : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800'
              }`}
              data-testid="filter-all"
            >
              <span>Semua Kategori</span>
              {!selectedCategory && <Check className="w-4 h-4" />}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setSelectedCategory(cat); setFilterDrawerOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  selectedCategory === cat
                    ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-medium'
                    : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800'
                }`}
                data-testid={`filter-${cat}`}
              >
                <span>{cat}</span>
                {selectedCategory === cat && <Check className="w-4 h-4" />}
              </button>
            ))}
            {categories.length === 0 && (
              <p className="text-center text-sm text-slate-500 py-4 dark:text-zinc-400">Belum ada kategori</p>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[550px] bg-white dark:bg-zinc-900 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Edit Produk
            </DialogTitle>
          </DialogHeader>
          <ProductForm 
            formData={formData}
            setFormData={setFormData}
            newVariant={newVariant}
            setNewVariant={setNewVariant}
            categories={categories}
            onSubmit={handleUpdateProduct} 
            onCancel={handleCloseDialog}
            submitLabel="Simpan Perubahan" 
            businessType={isDigitalCatalog ? 'digital' : 'physical'}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px] bg-white dark:bg-zinc-900">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2 dark:text-red-400">
              <Trash2 className="w-5 h-5" />
              Hapus Produk
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 dark:text-zinc-400">
            Apakah Anda yakin ingin menghapus <strong>{selectedProduct?.name}</strong>? Tindakan ini tidak dapat dibatalkan.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} data-testid="cancel-delete-btn">
              Batal
            </Button>
            <Button onClick={handleDeleteProduct} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              Hapus
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogPage;
